import { useMemo } from 'react';
import { useCachedFxRange } from './useCachedData';
import { calculateAllSystemScores } from '../util/SystemScoringUtil';
import bloodGroupings from '../../lambda/health-recommendations/blood_groupings.json';

/**
 * Hook to calculate organ system scores from test data
 * @param {Array<Object>} tests - Array of test objects with alias, result, lab_low, lab_high
 * @param {string} sex - Patient sex for fx range lookup ('male' or 'female')
 * @returns {Object} { systemScores, isLoading }
 */
export function useSystemScores(tests, sex = 'male') {
  const { fxRangeData, getFxRange } = useCachedFxRange();

  const systemScores = useMemo(() => {
    if (!tests || tests.length === 0 || !fxRangeData) {
      return null;
    }

    // Wrapper to get fx range for a marker
    const getFxRangeForMarker = (markerName) => {
      return getFxRange(markerName, sex);
    };

    return calculateAllSystemScores(tests, bloodGroupings, getFxRangeForMarker);
  }, [tests, fxRangeData, getFxRange, sex]);

  // Display order follows blood_groupings.json key order (single source of truth)
  const systemScoresArray = useMemo(() => {
    if (!systemScores) return [];
    return Object.keys(bloodGroupings)
      .filter(id => systemScores[id])
      .map(id => systemScores[id]);
  }, [systemScores]);

  return {
    systemScores,
    systemScoresArray,
    isLoading: !fxRangeData
  };
}

export default useSystemScores;
